// Client-side RRULE helpers — kept tiny and aligned with what the
// EventForm picker exposes. The server is the authority for expansion;
// here we only build the RRULE string we send back to it, and parse
// just enough to round-trip the picker when editing.

export type Frequency = "DAILY" | "WEEKLY" | "MONTHLY" | "YEARLY";

export interface PickerState {
  freq: Frequency | null; // null => no recurrence
  until: string; // YYYY-MM-DD or ""; "" means "no end"
}

export function buildRruleString(state: PickerState): string | null {
  if (!state.freq) return null;
  const parts = [`FREQ=${state.freq}`];
  if (state.until) {
    // UNTIL must be UTC per RFC 5545. We use the date alone (no time)
    // because the picker stores wall-clock dates.
    const compact = state.until.replace(/-/g, "");
    parts.push(`UNTIL=${compact}`);
  }
  return parts.join(";");
}

export function parsePickerStateFromRrule(rrule: string | null): PickerState {
  if (!rrule) return { freq: null, until: "" };
  const out: PickerState = { freq: null, until: "" };
  for (const part of rrule.replace(/^RRULE:/i, "").split(";")) {
    const eq = part.indexOf("=");
    if (eq < 0) continue;
    const key = part.slice(0, eq).toUpperCase();
    const value = part.slice(eq + 1);
    if (key === "FREQ") {
      const v = value.toUpperCase();
      if (v === "DAILY" || v === "WEEKLY" || v === "MONTHLY" || v === "YEARLY") {
        out.freq = v;
      }
    } else if (key === "UNTIL") {
      // YYYYMMDD or YYYYMMDDTHHMMSSZ
      const m = value.match(/^(\d{4})(\d{2})(\d{2})/);
      if (m) out.until = `${m[1]}-${m[2]}-${m[3]}`;
    }
  }
  return out;
}

export function formatFrequencyLabel(state: PickerState): string {
  if (!state.freq) return "Never";
  const base = {
    DAILY: "Daily",
    WEEKLY: "Weekly",
    MONTHLY: "Monthly",
    YEARLY: "Yearly",
  }[state.freq];
  if (state.until) return `${base} until ${state.until}`;
  return base;
}
